<template>
  <module-template
    id="scrolltainer"
    :title="$t('global.retention-policy.preview')"
  >
    <!-- Standalone Runs Card -->
    <v-card
      v-if="loadingStandaloneRuns || preview.standaloneRunsCount > 0"
      class="mb-4"
    >
      <v-card-title>
        {{ $t("global.retention-policy.standalone-runs") }}
        <span v-if="!loadingStandaloneRuns" class="ml-2"
          >({{ preview.standaloneRunsCount }})</span
        >
      </v-card-title>

      <v-skeleton-loader :loading="loadingStandaloneRuns" type="table">
        <v-card-text>
          <v-expansion-panels v-model="standaloneRunsPanel" focusable>
            <v-expansion-panel
              v-for="item in standaloneRunsPerEnv"
              :key="item.envLabel"
            >
              <v-expansion-panel-header>
                <v-row no-gutters align="center">
                  <v-col cols="1">
                    <file-env-chip
                      :color="item.envColor"
                      :label="item.envLabel"
                      class="ml-1"
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-icon small>pic-run-circle</v-icon>
                    {{ item.count }}
                    {{ $t("global.menu.runs").toLowerCase() }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ooliba-basic-table
                  :headers="runHeadersWithoutEnv"
                  :items="item.items"
                  item-key="id"
                  :items-per-page="10"
                  sort-by="id"
                >
                  <template v-slot:[`item.dateCreated`]="{ item }">
                    {{ formatDate(item.dateCreated) }}
                  </template>
                </ooliba-basic-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-skeleton-loader>
    </v-card>

    <!-- BP Instance Runs Card -->
    <v-card
      v-if="loadingBpInstanceRuns || preview.bpInstanceRunsCount > 0"
      class="mb-4"
    >
      <v-card-title>
        {{ $t("global.retention-policy.bp-instance-runs") }}
        <span v-if="!loadingBpInstanceRuns" class="ml-2"
          >({{ preview.bpInstanceRunsCount }})</span
        >
      </v-card-title>

      <v-skeleton-loader :loading="loadingBpInstanceRuns" type="table">
        <v-card-text>
          <v-expansion-panels v-model="bpInstanceRunsPanel" focusable>
            <v-expansion-panel
              v-for="item in bpInstanceRunsPerEnv"
              :key="item.envLabel"
            >
              <v-expansion-panel-header>
                <v-row no-gutters align="center">
                  <v-col cols="1">
                    <file-env-chip
                      :color="item.envColor"
                      :label="item.envLabel"
                      class="ml-1"
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-icon small>pic-run-circle</v-icon>
                    {{ item.count }}
                    {{ $t("global.menu.runs").toLowerCase() }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ooliba-basic-table
                  :headers="runHeadersWithoutEnv"
                  :items="item.items"
                  item-key="id"
                  :items-per-page="10"
                  sort-by="id"
                >
                  <template v-slot:[`item.dateCreated`]="{ item }">
                    {{ formatDate(item.dateCreated) }}
                  </template>
                </ooliba-basic-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-skeleton-loader>
    </v-card>

    <!-- BP Instances Card -->
    <v-card
      v-if="loadingBpInstances || preview.bpInstancesCount > 0"
      class="mb-4"
    >
      <v-card-title>
        {{ $t("global.retention-policy.bp-instances") }}
        <span v-if="!loadingBpInstances" class="ml-2"
          >({{ preview.bpInstancesCount }})</span
        >
      </v-card-title>

      <v-skeleton-loader :loading="loadingBpInstances" type="table">
        <v-card-text>
          <v-expansion-panels v-model="bpInstancesPanel" focusable>
            <v-expansion-panel
              v-for="item in bpInstancesPerEnv"
              :key="item.envLabel"
            >
              <v-expansion-panel-header>
                <v-row no-gutters align="center">
                  <v-col cols="1">
                    <file-env-chip
                      :color="item.envColor"
                      :label="item.envLabel"
                      class="ml-1"
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-icon small>pic-module</v-icon>
                    {{ item.count }}
                    {{
                      $t("global.business-process.bp-instances").toLowerCase()
                    }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ooliba-basic-table
                  :headers="instanceHeadersWithoutEnv"
                  :items="item.items"
                  item-key="id"
                  :items-per-page="10"
                  sort-by="id"
                >
                  <template v-slot:[`item.dateCreated`]="{ item }">
                    {{ formatDate(item.dateCreated) }}
                  </template>
                </ooliba-basic-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-skeleton-loader>
    </v-card>

    <!-- Unused Files Card -->
    <v-card
      v-if="loadingUnusedFiles || preview.unusedFilesCount > 0"
      class="mb-4"
    >
      <v-card-title>
        {{ $t("global.retention-policy.unused-files") }}
        <span v-if="!loadingUnusedFiles" class="ml-2"
          >({{ preview.unusedFilesCount }})</span
        >
      </v-card-title>

      <v-skeleton-loader :loading="loadingUnusedFiles" type="table">
        <v-card-text>
          <v-expansion-panels v-model="unusedFilesPanel" focusable>
            <v-expansion-panel
              v-for="item in unusedFilesPerEnv"
              :key="item.envLabel"
            >
              <v-expansion-panel-header>
                <v-row no-gutters align="center">
                  <v-col cols="1">
                    <file-env-chip
                      :color="item.envColor"
                      :label="item.envLabel"
                      class="ml-1"
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-icon small>pic-contract</v-icon>
                    {{ item.count }}
                    {{ $t("global.menu.files").toLowerCase() }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ooliba-basic-table
                  :headers="fileHeadersWithoutEnv"
                  :items="item.items"
                  item-key="id"
                  :items-per-page="10"
                  sort-by="id"
                >
                  <template v-slot:[`item.dateCreated`]="{ item }">
                    {{ formatDate(item.dateCreated) }}
                  </template>
                </ooliba-basic-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-skeleton-loader>
    </v-card>

    <template v-if="allLoaded">
      <v-alert v-if="totalItems > 0" type="warning" class="mb-4">
        {{ $t("global.retention-policy.confirm-warning") }}
      </v-alert>

      <v-alert v-else type="info" class="mb-4">
        {{ $t("global.retention-policy.no-deletion-info") }}
      </v-alert>
    </template>

    <div class="mt-4">
      <v-btn class="mr-2" @click="goBack">
        {{ $t("global.action.cancel") }}
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!allLoaded"
        :loading="saving"
        @click="confirmSave"
      >
        {{ $t("global.retention-policy.confirm-button") }}
      </v-btn>
    </div>
  </module-template>
</template>

<script>
import { get, post, put } from "@/model/api";
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import OolibaBasicTable from "@/components/OolibaBasicTable";
import FileEnvChip from "@/components/FileEnvChip";

const SESSION_STORAGE_KEY = "retention-policy-pending-save";

export default {
  name: "RetentionPolicyPreview",
  components: {
    ModuleTemplate,
    OolibaBasicTable,
    FileEnvChip,
  },

  computed: {
    allLoaded() {
      return (
        !this.loadingStandaloneRuns &&
        !this.loadingBpInstanceRuns &&
        !this.loadingBpInstances &&
        !this.loadingUnusedFiles
      );
    },
    totalItems() {
      return (
        this.preview.standaloneRunsCount +
        this.preview.bpInstanceRunsCount +
        this.preview.bpInstancesCount +
        this.preview.unusedFilesCount
      );
    },
    isConfirmMode() {
      return this.mode === "confirm-file-env" || this.mode === "confirm-policy";
    },
    runHeadersWithoutEnv() {
      return [
        { text: "ID", value: "id" },
        { text: this.$t("global.concepts.title"), value: "title" },
        { text: this.$t("global.concepts.status"), value: "status" },
        {
          text: this.$t("global.administration.creation-date"),
          value: "dateCreated",
        },
      ];
    },
    instanceHeadersWithoutEnv() {
      return [
        { text: "ID", value: "id" },
        { text: this.$t("global.concepts.title"), value: "title" },
        {
          text: this.$t("global.administration.creation-date"),
          value: "dateCreated",
        },
      ];
    },
    fileHeadersWithoutEnv() {
      return [
        { text: "ID", value: "id" },
        { text: this.$t("global.concepts.name"), value: "name" },
        { text: this.$t("global.concepts.version"), value: "versionNumber" },
        {
          text: this.$t("global.administration.creation-date"),
          value: "dateCreated",
        },
      ];
    },
    standaloneRunsPerEnv() {
      return this.groupByEnv(this.preview.standaloneRuns);
    },
    bpInstanceRunsPerEnv() {
      return this.groupByEnv(this.preview.bpInstanceRuns);
    },
    bpInstancesPerEnv() {
      return this.groupByEnv(this.preview.bpInstances);
    },
    unusedFilesPerEnv() {
      return this.groupByEnv(this.preview.unusedFiles);
    },
  },

  data() {
    return {
      loadingStandaloneRuns: true,
      loadingBpInstanceRuns: true,
      loadingBpInstances: true,
      loadingUnusedFiles: true,
      saving: false,
      standaloneRunsPanel: undefined,
      bpInstanceRunsPanel: undefined,
      bpInstancesPanel: undefined,
      unusedFilesPanel: undefined,
      mode: null,
      fileEnvId: null,
      pendingData: null,
      fileEnvs: [],
      preview: {
        standaloneRunsCount: 0,
        standaloneRuns: [],
        bpInstanceRunsCount: 0,
        bpInstanceRuns: [],
        bpInstancesCount: 0,
        bpInstances: [],
        unusedFilesCount: 0,
        unusedFiles: [],
      },
    };
  },

  methods: {
    async getPreviewForPolicy(policyId, category) {
      const requestBody = {
        standaloneRuns: this.pendingData.standaloneRuns,
        bpInstanceRuns: this.pendingData.bpInstanceRuns,
        bpInstances: this.pendingData.bpInstances,
        unusedFiles: this.pendingData.unusedFiles,
      };
      const url = category
        ? "/retention-policy/" + policyId + "/preview?category=" + category
        : "/retention-policy/" + policyId + "/preview";
      return await post(url, requestBody).catch((error) => {
        this.$store.commit("showError", error);
      });
    },

    async getPreviewForFileEnv(fileEnvId, policyId, category) {
      const url = category
        ? "/retention-policy/file-env/" +
          fileEnvId +
          "/preview/" +
          policyId +
          "?category=" +
          category
        : "/retention-policy/file-env/" + fileEnvId + "/preview/" + policyId;
      return await get(url).catch((error) => {
        this.$store.commit("showError", error);
      });
    },

    async fetchStandaloneRuns(policyId) {
      let result;
      if (this.mode === "confirm-file-env" && this.fileEnvId) {
        result = await this.getPreviewForFileEnv(
          this.fileEnvId,
          policyId,
          "standaloneRuns"
        );
      } else {
        result = await this.getPreviewForPolicy(policyId, "standaloneRuns");
      }
      if (result) {
        this.preview.standaloneRunsCount = result.standaloneRunsCount;
        this.preview.standaloneRuns = result.standaloneRuns;
      }
      this.loadingStandaloneRuns = false;
    },

    async fetchBpInstanceRuns(policyId) {
      let result;
      if (this.mode === "confirm-file-env" && this.fileEnvId) {
        result = await this.getPreviewForFileEnv(
          this.fileEnvId,
          policyId,
          "bpInstanceRuns"
        );
      } else {
        result = await this.getPreviewForPolicy(policyId, "bpInstanceRuns");
      }
      if (result) {
        this.preview.bpInstanceRunsCount = result.bpInstanceRunsCount;
        this.preview.bpInstanceRuns = result.bpInstanceRuns;
      }
      this.loadingBpInstanceRuns = false;
    },

    async fetchBpInstances(policyId) {
      let result;
      if (this.mode === "confirm-file-env" && this.fileEnvId) {
        result = await this.getPreviewForFileEnv(
          this.fileEnvId,
          policyId,
          "bpInstances"
        );
      } else {
        result = await this.getPreviewForPolicy(policyId, "bpInstances");
      }
      if (result) {
        this.preview.bpInstancesCount = result.bpInstancesCount;
        this.preview.bpInstances = result.bpInstances;
      }
      this.loadingBpInstances = false;
    },

    async fetchUnusedFiles(policyId) {
      let result;
      if (this.mode === "confirm-file-env" && this.fileEnvId) {
        result = await this.getPreviewForFileEnv(
          this.fileEnvId,
          policyId,
          "unusedFiles"
        );
      } else {
        result = await this.getPreviewForPolicy(policyId, "unusedFiles");
      }
      if (result) {
        this.preview.unusedFilesCount = result.unusedFilesCount;
        this.preview.unusedFiles = result.unusedFiles;
      }
      this.loadingUnusedFiles = false;
    },

    async loadFileEnvs() {
      return await get("/file-env").catch((error) => {
        this.$store.commit("showError", error);
      });
    },

    groupByEnv(items) {
      if (!items || items.length === 0) {
        return [];
      }

      const grouped = {};
      for (const item of items) {
        const envName = item.fileEnv;
        if (!grouped[envName]) {
          const env = this.fileEnvs.find((e) => e.name === envName);
          grouped[envName] = {
            envLabel: env?.label || envName,
            envColor: env?.color || null,
            count: 0,
            items: [],
          };
        }
        grouped[envName].count++;
        grouped[envName].items.push(item);
      }

      return Object.values(grouped);
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      return new Date(dateString).toLocaleString();
    },

    goBack() {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      if (this.mode === "confirm-file-env") {
        this.$router.push({
          name: "Edit file env",
          params: { fileEnvId: this.fileEnvId },
        });
      } else {
        this.$router.push({
          name: "Edit retention policy",
          params: { policyId: this.$route.params.policyId },
        });
      }
    },

    async confirmSave() {
      if (!this.pendingData) {
        this.$store.commit("showError", "No pending data to save");
        return;
      }

      this.saving = true;
      try {
        if (this.mode === "confirm-file-env") {
          await put("/file-env", this.pendingData);
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
          this.$router.push({ name: "File envs" });
        } else if (this.mode === "confirm-policy") {
          await put(
            "/retention-policy/" + this.$route.params.policyId,
            this.pendingData
          );
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
          this.$router.push({ name: "Retention policies" });
        }
      } catch (error) {
        this.$store.commit("showError", error);
      } finally {
        this.saving = false;
      }
    },
  },

  async created() {
    const policyId = this.$route.params.policyId;
    this.mode = this.$route.query.mode || null;
    this.fileEnvId = this.$route.query.fileEnvId || null;

    // This component only works in confirm mode
    if (!this.isConfirmMode) {
      this.$router.push({ name: "Retention policies" });
      return;
    }

    // Load pending data from sessionStorage
    const storedData = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (storedData) {
      this.pendingData = JSON.parse(storedData);
    } else {
      this.$store.commit("showError", "No pending data found");
      this.goBack();
      return;
    }

    // Fetch file environments for color mapping
    const envs = await this.loadFileEnvs();
    if (envs) {
      this.fileEnvs = envs;
    }

    // Launch all 4 requests in parallel (no await)
    this.fetchStandaloneRuns(policyId);
    this.fetchBpInstanceRuns(policyId);
    this.fetchBpInstances(policyId);
    this.fetchUnusedFiles(policyId);
  },
};
</script>
